/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Root as CheckboxRoot, Indicator as CheckboxIndicator } from '@radix-ui/react-checkbox';
import { CheckIcon, MinusIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * All props accepted by `Checkbox` — every configurable parameter exposed by
 * Radix's [`CheckboxPrimitive.Root`](https://www.radix-ui.com/primitives/docs/components/checkbox)
 * (e.g. `checked`, `defaultChecked`, `onCheckedChange`, `disabled`, `required`,
 * `name`, `value`, `id`, `aria-*`), plus standard HTML props.
 *
 * `checked` and `defaultChecked` accept `true`, `false`, or `'indeterminate'`,
 * matching the Figma `Checked = False | True | Intermediate` variants. Note
 * the prop value is spelt `'indeterminate'` because that is the standard
 * HTML/ARIA/Radix term — it is equivalent to Figma's `Intermediate` variant.
 */
export type CheckboxProps = ComponentPropsWithoutRef<typeof CheckboxRoot>;

/**
 * `Checkbox` is the design-system checkbox from the Storefront NEXT Figma
 * specification (16×16, 2 px radius, 1 px border). It is a thin wrapper around
 * [`@radix-ui/react-checkbox`](https://www.radix-ui.com/primitives/docs/components/checkbox)
 * that applies the project's design tokens from `src/theme/` — no hard-coded
 * colours.
 *
 * Supports all three Figma `Checked` states:
 * - `false` → empty box with `border-border` border. (Figma: **False**)
 * - `true` → filled `bg-primary` box with a check glyph. (Figma: **True**)
 * - `'indeterminate'` → filled `bg-primary` box with a minus glyph.
 *   (Figma: **Intermediate** — same state, different spelling.)
 *
 * And both Figma `State` variants:
 * - `Enabled` → default styling.
 * - `Focus` → 3 px `ring-focus` outline + `border-ring`.
 *
 * @example
 * ```tsx
 * <Checkbox defaultChecked />
 * // Figma "Intermediate" maps to the HTML/ARIA value 'indeterminate':
 * <Checkbox checked="indeterminate" onCheckedChange={setChecked} />
 * <label className="flex items-center gap-2">
 *     <Checkbox id="terms" />
 *     <span>Accept terms</span>
 * </label>
 * ```
 */
const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox({ className, ...props }, ref) {
    return (
        <CheckboxRoot
            ref={ref}
            data-slot="checkbox"
            className={cn(
                'peer size-4 shrink-0 rounded-[2px] border border-border bg-background outline-none',
                'transition-[color,background-color,border-color,box-shadow]',
                'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
                'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
                'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-focus',
                'aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive-focus',
                'disabled:cursor-not-allowed disabled:opacity-50',
                '[&[data-state=indeterminate]_[data-slot=checkbox-check]]:hidden',
                '[&[data-state=checked]_[data-slot=checkbox-minus]]:hidden',
                className
            )}
            {...props}>
            <CheckboxIndicator
                data-slot="checkbox-indicator"
                className="grid size-full place-content-center text-current">
                <CheckIcon data-slot="checkbox-check" className="size-3.5" strokeWidth={2} aria-hidden="true" />
                <MinusIcon data-slot="checkbox-minus" className="size-3.5" strokeWidth={2} aria-hidden="true" />
            </CheckboxIndicator>
        </CheckboxRoot>
    );
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
export { Checkbox };
